import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import App from './App';
import './index.scss';
import { SENTRY_DSN } from './config/sentry';
import { AppProvider } from './providers/app';
import storage from './utils/storage';

const getEnv = () => {
  const { hostname } = window.location;
  if (hostname.includes('localhost')) {
    return 'development';
  }
  if (hostname.includes('rc')) {
    return 'release';
  }
  if (hostname.includes('stage')) {
    return 'stage';
  }
  if (hostname.includes('sanity')) {
    return 'sanity';
  }
  return 'production';
};

Sentry.init({
  dsn: SENTRY_DSN,
  environment: getEnv(),
  integrations: [
    new Sentry.BrowserTracing({
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      // tracePropagationTargets: ['localhost', 'https:rest..io/api/'],
      tracingOrigins: ['localhost', /.*hungerbox.com.*/],
      routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      ),
    }),
    new Sentry.Replay(),
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1, // Capture 100% of the transactions, reduce in production!
  replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 0.1, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  beforeSend: (event) => {
    if (event?.environment === 'development') {
      return null;
    }
    return event;
  },
});

Sentry.setUser({
  id: storage.getItem('currentUser')?.id ?? null,
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <AppProvider>
      <App />
    </AppProvider>
  </React.StrictMode>
);
